import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import './AddWebsite.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

const getStatusIcon = (type) => {
  switch (type) {
    case 'update':
      return '⏳';
    case 'finish':
      return '✅';
    case 'error':
      return '❌';
    default:
      return 'ℹ️';
  }
};

export default function AddWebsite({ onWebsiteAdded }) {
  // ------------------------------------------------------------------ form state
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [url, setUrl] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------------ ws helpers
  const wsRef = useRef(null);
  const [wsDomain, setWsDomain] = useState(null); // domain this tab is watching

  // ------------------------------------------------------------------ loading text spinner
  const loadingMsgs = useMemo(
    () => [
      'Initializing AI systems…',
      'Loading processing modules…',
      'Warming up the engine…',
      'Tuning neural network…',
      'Connecting to the AI backend…',
      'Running system checks…',
      'Preparing data for analysis…',
      'Optimizing pipeline performance…',
      'Synchronizing model parameters…',
      'Crunching input data…',
    ],
    []
  );
  const [buttonText, setButtonText] = useState('Create Demo');

  useEffect(() => {
    if (!loading) return setButtonText('Create Demo');
    let idx = 0;
    setButtonText(loadingMsgs[0]);
    const id = setInterval(() => {
      idx = (idx + 1) % loadingMsgs.length;
      setButtonText(loadingMsgs[idx]);
    }, 2500);
    return () => clearInterval(id);
  }, [loading, loadingMsgs]);

  // ------------------------------------------------------------------ WebSocket setup
  useEffect(() => {
    if (!wsDomain) return undefined; // nothing to watch yet

    const wsUrl = `${BACKEND_URL.replace(/^http/, 'ws')}/ws/updates?domain=${encodeURIComponent(
      wsDomain
    )}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = ({ data }) => {
      let msg;
      try {
        msg = JSON.parse(data);
      } catch {
        return;
      }
      if (!msg.type || msg.type === 'ping') return;
      if (msg.domain !== wsDomain) return; // ignore broadcasts for other domains

      setStatus({ type: msg.type, text: msg.text });

      if (msg.type === 'finish' || msg.type === 'error') {
        setLoading(false);
        onWebsiteAdded(wsDomain, msg.urls || [], msg.type);
      }
    };

    ws.onerror = () => {
      if (loading) {
        setStatus({ type: 'error', text: 'Connection lost.' });
        setLoading(false);
        onWebsiteAdded(wsDomain, [], 'error');
      }
    };

    return () => ws.readyState < 2 && ws.close();
  }, [wsDomain, loading, onWebsiteAdded]);

  // ------------------------------------------------------------------ field validation
  const validateFields = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Name is required.';
    if (!company.trim()) errs.company = 'Company is required.';
    if (!url.trim()) {
      errs.url = 'URL is required.';
    } else {
      try {
        let norm = url.trim();
        if (!/^[a-zA-Z][\w.+-]*:/.test(norm)) {
          norm = `https://${norm}`;
        }
        new URL(norm);
      } catch {
        errs.url = 'Must be a valid URL.';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ------------------------------------------------------------------ submit handler
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (loading) return;
      if (!validateFields()) return;

      // normalize URL
      let normalized = url.trim();
      if (!/^[a-zA-Z][\w.+-]*:/.test(normalized)) {
        normalized = `https://${normalized}`;
      }
      setUrl(normalized);

      setLoading(true);
      setStatus({ type: 'update', text: 'Submitting…' });

      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/chatbot/add_website`,
          {
            website_url: normalized,
            company_name: company.trim(),
            person_name: name.trim(),
          }
        );

        const domain =
          data.domain ||
          new URL(normalized).hostname.replace(/^www\./, '');

        // ensure we’re listening on the right socket
        if (wsDomain !== domain) {
          // close previous socket if any
          wsRef.current?.readyState < 2 && wsRef.current.close();
          setWsDomain(domain);
        }

        // ----------------- already-exists (immediate finish)
        if (data.success && data.message?.toLowerCase().includes('already')) {
          setStatus({ type: 'finish', text: data.message });
          setLoading(false);
          onWebsiteAdded(domain, data.urls || [], 'finish');
        }
        // ----------------- server error
        else if (data.error || data.detail) {
          setStatus({ type: 'error', text: data.error || data.detail });
          setLoading(false);
          onWebsiteAdded(domain, [], 'error');
        }
        // ----------------- brand-new scrape returned finish immediately
        else if (data.finish) {
          setStatus({ type: 'finish', text: data.finish });
          setLoading(false);
          onWebsiteAdded(domain, data.urls || [], 'finish');
        }
        // else we’ll wait for WS updates
      } catch (err) {
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          'Submission failed.';
        setStatus({ type: 'error', text: msg });
        setLoading(false);
        onWebsiteAdded(url, [], 'error');
      }
    },
    [name, company, url, loading, onWebsiteAdded, wsDomain]
  );

  // ------------------------------------------------------------------ helpers
  const onChangeField = (field, setter) => (e) => {
    setter(e.target.value);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setStatus(null);
  };

  const canSubmit = !loading && !errors.name && !errors.company && !errors.url;

  // ------------------------------------------------------------------ render
  return (
    <div className="add-website">
      <p className="form-intro">
        <strong>Step&nbsp;1</strong> – Provide your details and website link
        below. We’ll build a demo chatbot based off a few website pages as a
        sample of the full chatbot.
      </p>

      <form onSubmit={handleSubmit} noValidate className="add-website-form">
        {/* name --------------------------------------------------------- */}
        <div className="field-group">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            value={name}
            placeholder="Your name"
            onChange={onChangeField('name', setName)}
            disabled={loading}
            className="add-website-input"
          />
          {errors.name && <div className="field-error">{errors.name}</div>}
        </div>

        {/* company ------------------------------------------------------ */}
        <div className="field-group">
          <label htmlFor="company">Company</label>
          <input
            id="company"
            type="text"
            value={company}
            placeholder="Your company name"
            onChange={onChangeField('company', setCompany)}
            disabled={loading}
            className="add-website-input"
          />
          {errors.company && (
            <div className="field-error">{errors.company}</div>
          )}
        </div>

        {/* url ---------------------------------------------------------- */}
        <div className="field-group">
          <label htmlFor="website">Website URL</label>
          <input
            id="website"
            type="url"
            value={url}
            placeholder="https://example.com"
            onChange={onChangeField('url', setUrl)}
            disabled={loading}
            className="add-website-input"
          />
          {errors.url && <div className="field-error">{errors.url}</div>}
        </div>

        <button
          type="submit"
          className="add-website-button"
          disabled={!canSubmit}
        >
          {buttonText}
        </button>
      </form>

      {status && (
        <div className={`website-status ${status.type}`}>
          <span className="status-icon">{getStatusIcon(status.type)}</span>
          <span className="status-text">{status.text}</span>
        </div>
      )}
    </div>
  );
}

AddWebsite.propTypes = {
  onWebsiteAdded: PropTypes.func.isRequired,
};
